import { Component, inject, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import {
  TableActionConfig,
  TableColumn,
  TableConfig,
  TableDateRangeEvent,
  TableFilterChangeEvent,
  TablePagination,
  TableSearchEvent,
  TableSelectionChangeEvent,
  TableSort,
} from "../../../../Common/Components/Table/Type/TableType";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { RequestEffects } from "../../State/RequestEffects";
import { RequestState } from "../../State/RequestState";
import { RequestEntity } from "../../Dto/RequestModel";
import { formatDateNumeric } from "../../../../Common/Utility/UtilityDate";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { RequestExpandedRowComponent } from "../../Components/RequestExpandedRow/RequestExpandedRowComponent";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";

@Component({
  selector: "app-request-home",
  standalone: true,
  imports: [AppTableComponent, RequestExpandedRowComponent, AppButtonComponent],
  templateUrl: "./RequestHomePage.html",
  styleUrl: "./RequestHomePage.scss",
  encapsulation: ViewEncapsulation.None,
})
export class RequestHomePage implements OnInit {
  private readonly requestEffects = inject(RequestEffects);
  readonly requestState = inject(RequestState);
  private readonly router = inject(Router);
  private readonly toastEffects = inject(ToastEffects);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  @ViewChild("requestExpandedRow", { static: true }) requestExpandedRowTemplate!: TemplateRef<{ $implicit: RequestEntity }>;
  selectedStatusFilter = "";
  selectedRequestIds: string[] = [];
  readonly requestStatusFilterOptions: { value: RequestEntity["status"]; label: string }[] = [
    { value: "Draft", label: "Draft" },
    { value: "Confirmed", label: "Ready for Packing" },
    { value: "Packed", label: "In Shipment" },
    { value: "Dispatched", label: "In Transit" },
    { value: "Delivered", label: "Delivered" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  requestColumns: TableColumn<RequestEntity>[] = [
    { key: "requestNo", header: "Request No", sortable: true, width: "18%" },
    {
      key: "teamLeader.name",
      header: "Team Leader",
      sortable: false,
      width: "18%",
      cellTemplate: (item) => item.teamLeader?.name || "-",
    },
    {
      key: "partner.name",
      header: "Partner",
      sortable: false,
      width: "18%",
      cellTemplate: (item) => item.partner?.name || "-",
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "14%",
      cellTemplate: (item) => this.getStatusLabel(item.status),
      cellClass: (item) => this.getStatusChipClass(item.status),
      cellIcon: (item) => this.getStatusChipIcon(item.status),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "10%",
      cellTemplate: (item) =>
        (() => {
          if (item.createdAt) {
            return formatDateNumeric(item.createdAt);
          }
          return "-";
        })(),
    },
  ];

  get requestConfig(): TableConfig<RequestEntity> {
    return {
      loading: this.requestState.loading(),
      emptyMessage: this.requestState.error(),
      showActions: true,
      actionsWidth: "220px",
      showSearch: true,
      searchPlaceholder: "Search requests...",
      showCreate: true,
      createButtonLabel: "Create Request",
      createButtonIcon: "add",
      showDateRangeFilter: true,
      dateRangeFilterLabel: "Filter by creation date",
      filters: [
        {
          key: "status",
          label: "Status",
          placeholder: "All status",
          options: this.requestStatusFilterOptions,
          defaultValue: this.selectedStatusFilter,
        },
      ],
      expandable: {
        enabled: true,
        allowMultipleExpanded: true,
        getRowId: (item) => item.id,
        expandedRowTemplate: this.requestExpandedRowTemplate,
        expandButtonLabel: "Show request details",
      },
      selection: {
        enabled: true,
        getRowId: (item) => item.id,
        selectedRowIds: this.selectedRequestIds,
        selectable: (item) => item.status === "Confirmed",
      },
      onCreate: () => this.createRequest(),
      actionButtons: [
        { label: "edit", icon: "edit", handler: (item) => this.editRequest(item) },
        {
          label: "confirm",
          icon: "check",
          visible: (item) => item.status === "Draft",
          handler: (item) => this.openConfirmRequestModal(item),
        },
        {
          label: "delete",
          icon: "delete",
          visible: (item) => item.status === "Draft",
          handler: (item) => this.openDeleteRequestModal(item),
        },
      ],
    };
  }

  get requestPagination(): TablePagination {
    return this.requestState.pagination();
  }

  ngOnInit(): void {
    const current = this.requestState.searchParams();
    this.selectedStatusFilter = current.status ?? "";
    this.requestEffects.searchRequests(current);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: TableSort | null): void {
    return;
  }

  onPageChange(event: { page: number; search?: string }): void {
    const current = this.requestState.searchParams();
    this.requestEffects.searchRequests({ ...current, page: event.page, search: event.search });
  }

  onPageSizeChange(event: { limit: number; search?: string }): void {
    const current = this.requestState.searchParams();
    this.requestEffects.searchRequests({ ...current, page: 1, limit: event.limit, search: event.search });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    return;
  }

  onSearchChange(event: TableSearchEvent): void {
    const current = this.requestState.searchParams();
    this.requestEffects.searchRequests({
      ...current,
      search: event.term,
      page: event.page,
    });
  }

  onFilterChange(event: TableFilterChangeEvent): void {
    this.selectedStatusFilter = event.filters["status"] ?? "";

    const current = this.requestState.searchParams();
    const nextStatus = this.selectedStatusFilter.trim() as RequestEntity["status"] | "";
    if (!nextStatus) {
      this.requestEffects.searchRequests({
        ...current,
        status: undefined,
        page: 1,
      });
      return;
    }

    this.requestEffects.searchRequests({
      ...current,
      status: nextStatus,
      page: 1,
    });
  }

  onDateRangeChange(event: TableDateRangeEvent): void {
    const current = this.requestState.searchParams();
    this.requestEffects.searchRequests({
      ...current,
      page: event.page,
      createdAtFrom: event.dateRange.from,
      createdAtTo: event.dateRange.to,
    });
  }

  onSelectionChange(event: TableSelectionChangeEvent): void {
    this.selectedRequestIds = event.selectedRowIds;
  }

  createRequest(): void {
    this.router.navigate(["/requests/create"]).catch(() => undefined);
  }

  createShipmentFromSelection(): void {
    if (this.selectedRequestIds.length === 0) {
      this.toastEffects.show("Select confirmed requests to start a shipment.", "error");
      return;
    }

    this.router
      .navigate(["/shipments/create"], {
        queryParams: {
          requestIds: this.selectedRequestIds.join(","),
        },
      })
      .catch(() => undefined);
  }

  editRequest(item: RequestEntity): void {
    this.router.navigate(["/requests/edit", item.id]).catch(() => undefined);
  }

  async confirmRequest(item: RequestEntity): Promise<void> {
    try {
      await this.requestEffects.confirmRequest(item.id);
      this.toastEffects.show("Request confirmed.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to confirm request."), "error");
    }
  }

  openConfirmRequestModal(item: RequestEntity): void {
    this.confirmModalEffects.open({
      title: "Confirm Request",
      message: `Are you sure you want to confirm request "${item.requestNo}"?`,
      confirmText: "Confirm Request",
      cancelText: "Cancel",
      variant: "primary",
      closeOnOverlay: true,
      onConfirm: async () => {
        await this.confirmRequest(item);
      },
    });
  }

  async deleteRequest(item: RequestEntity): Promise<void> {
    try {
      await this.requestEffects.deleteRequest(item.id);
      this.toastEffects.show("Draft request deleted.", "success");
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to delete draft request."), "error");
    }
  }

  openDeleteRequestModal(item: RequestEntity): void {
    this.confirmModalEffects.open({
      title: "Delete Draft Request",
      message: `Delete draft request "${item.requestNo}"? This cannot be undone.`,
      confirmText: "Delete Draft",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        await this.deleteRequest(item);
      },
    });
  }

  private getStatusChipClass(status: RequestEntity["status"]): string {
    if (status === "Draft") {
      return "app-table__chip app-table__chip--neutral";
    }
    if (status === "Confirmed") {
      return "app-table__chip app-table__chip--info";
    }
    if (status === "Packed") {
      return "app-table__chip app-table__chip--warning";
    }
    if (status === "Dispatched") {
      return "app-table__chip app-table__chip--info";
    }
    if (status === "Delivered") {
      return "app-table__chip app-table__chip--success";
    }
    return "app-table__chip app-table__chip--danger";
  }

  private getStatusLabel(status: RequestEntity["status"]): string {
    return this.requestStatusFilterOptions.find((option) => option.value === status)?.label ?? status;
  }

  private getStatusChipIcon(status: RequestEntity["status"]): string {
    if (status === "Draft") {
      return "schedule";
    }
    if (status === "Confirmed") {
      return "check_circle";
    }
    if (status === "Packed") {
      return "inventory_2";
    }
    if (status === "Dispatched") {
      return "local_shipping";
    }
    if (status === "Delivered") {
      return "verified";
    }
    return "cancel";
  }
}
